import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { FiMail, FiLock, FiEye, FiEyeOff, FiAlertCircle, FiUser, FiHash, FiArrowLeft, FiCheck } from 'react-icons/fi';
import './Login.css';

const Login = () => {
  const { login, studentLogin, studentVerify, resendVerificationCode, error, setError } = useAuth();
  const { isDarkMode } = useTheme();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [studentId, setStudentId] = useState('');
  const [studentEmail, setStudentEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState(['', '', '', '', '', '']);
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loginType, setLoginType] = useState('faculty'); // 'faculty' or 'student'
  const [verificationStep, setVerificationStep] = useState(false);
  const [verificationData, setVerificationData] = useState(null);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [successMessage, setSuccessMessage] = useState('');
  
  const codeInputRefs = useRef([]);

  // Countdown timer for resend button
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setSuccessMessage('');

    let result;
    if (loginType === 'faculty') {
      result = await login(email, password);
    } else {
      result = await studentLogin(studentId, studentEmail);
      
      if (result.success && result.codeSent) {
        setVerificationData({
          studentId: result.studentId,
          email: result.email,
        });
        setVerificationStep(true);
        setResendCooldown(60); // 60 seconds cooldown
        setIsLoading(false);
        return;
      }
    }
    
    if (!result.success) {
      setIsLoading(false);
    }
  };

  const handleVerificationSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setSuccessMessage('');

    const code = verificationCode.join('');
    if (code.length !== 6) {
      setError('Please enter the complete 6-digit verification code.');
      setIsLoading(false);
      return;
    }

    const result = await studentVerify(
      verificationData.studentId,
      verificationData.email,
      code
    );

    if (!result.success) {
      setIsLoading(false);
      // Clear the code on error
      setVerificationCode(['', '', '', '', '', '']);
      codeInputRefs.current[0]?.focus();
    }
  };

  const handleResendCode = async () => {
    if (resendCooldown > 0) return;
    
    setIsLoading(true);
    setSuccessMessage('');
    
    const result = await resendVerificationCode(
      verificationData.studentId,
      verificationData.email
    );

    setIsLoading(false);
    
    if (result.success) {
      setResendCooldown(60);
      setSuccessMessage('New verification code sent to your email.');
      setVerificationCode(['', '', '', '', '', '']);
      codeInputRefs.current[0]?.focus();
    }
  };

  const handleBackToLogin = () => {
    setVerificationStep(false);
    setVerificationData(null);
    setVerificationCode(['', '', '', '', '', '']);
    setError(null);
    setSuccessMessage('');
  };

  const toggleLoginType = (type) => {
    setLoginType(type);
    setError(null);
    setSuccessMessage('');
    setVerificationStep(false);
    setVerificationData(null);
    // Clear form fields when switching
    setEmail('');
    setPassword('');
    setStudentId('');
    setStudentEmail('');
    setVerificationCode(['', '', '', '', '', '']);
  };

  // Clear error when user starts typing
  const handleInputChange = (setter) => (e) => {
    if (error) {
      setError(null);
    }
    setSuccessMessage('');
    setter(e.target.value);
  };

  // Handle verification code input
  const handleCodeChange = (index, value) => {
    if (error) setError(null);
    setSuccessMessage('');
    
    // Only allow digits
    if (value && !/^\d$/.test(value)) return;

    const newCode = [...verificationCode];
    newCode[index] = value;
    setVerificationCode(newCode);

    // Auto-focus next input
    if (value && index < 5) {
      codeInputRefs.current[index + 1]?.focus();
    }
  };

  const handleCodeKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === 'Backspace' && !verificationCode[index] && index > 0) {
      codeInputRefs.current[index - 1]?.focus();
    }
  };

  const handleCodePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pastedData) {
      const newCode = [...verificationCode];
      for (let i = 0; i < pastedData.length && i < 6; i++) {
        newCode[i] = pastedData[i];
      }
      setVerificationCode(newCode);
      
      // Focus the next empty input or the last one
      const nextEmptyIndex = newCode.findIndex(c => !c);
      if (nextEmptyIndex !== -1) {
        codeInputRefs.current[nextEmptyIndex]?.focus();
      } else {
        codeInputRefs.current[5]?.focus();
      }
    }
  };

  return (
    <div className={`login-page ${isDarkMode ? 'dark-mode' : ''}`}>
      <div className="login-container">
        {/* Left Panel - Branding */}
        <div className="login-branding">
          <div className="branding-content">
            <img src="/logo.png" alt="CCS ChatBot Logo" className="login-logo" />
            <h1>COLLEGE OF COMPUTER SCIENCE</h1>
            <p className="tagline">4TH YEAR STUDENTS AI - POWERED CHATBOT INFORMATION SYSTEM</p>
            <div className="branding-divider"></div>
            <div className="institution">
              <p className="school">Osmeña Colleges</p>       
            </div>
          </div>
        </div>

        {/* Right Panel - Login Form */}
        <div className="login-form-container">
          <div className="login-form-wrapper">
            {!verificationStep ? (
              <>
                <div className="login-header">
                  <h2>Welcome Back</h2>
                  <p>Sign in to access the Student Information System</p>
                </div>

                {/* Login Type Toggle */}
                <div className="login-type-toggle">
                  <button 
                    className={`toggle-btn ${loginType === 'faculty' ? 'active' : ''}`}
                    onClick={() => toggleLoginType('faculty')}
                    type="button"
                  >
                    <FiUser /> Faculty
                  </button>
                  <button 
                    className={`toggle-btn ${loginType === 'student' ? 'active' : ''}`}
                    onClick={() => toggleLoginType('student')}
                    type="button"
                  >
                    <FiUser /> Student
                  </button>
                </div>

                {error && (
                  <div className="error-alert">
                    <FiAlertCircle />
                    <span>{error}</span>
                    <button 
                      type="button" 
                      className="error-close"
                      onClick={() => setError(null)}
                      title="Dismiss"
                    >
                      ×
                    </button>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="login-form">
                  {loginType === 'faculty' ? (
                    <>
                      <div className="form-group">
                        <label htmlFor="email">Faculty Email</label>
                        <div className={`input-wrapper ${email ? 'has-value' : ''}`}>
                          <FiMail className="input-icon" />
                          <input
                            type="email"
                            id="email"
                            value={email}
                            onChange={handleInputChange(setEmail)}
                            placeholder="Enter your email"
                            required
                            autoComplete="email"
                          />
                        </div>
                      </div>

                      <div className="form-group">
                        <label htmlFor="password">Password</label>
                        <div className={`input-wrapper ${password ? 'has-value' : ''}`}>
                          <FiLock className="input-icon" />
                          <input
                            type={showPassword ? 'text' : 'password'}
                            id="password"
                            value={password}
                            onChange={handleInputChange(setPassword)}
                            placeholder="Enter your password"
                            required
                            autoComplete="current-password"
                          />
                          <button
                            type="button"
                            className="password-toggle"
                            onClick={() => setShowPassword(!showPassword)}
                          >
                            {showPassword ? <FiEyeOff /> : <FiEye />}
                          </button>
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="form-group">
                        <label htmlFor="studentId">Student ID</label>
                        <div className={`input-wrapper ${studentId ? 'has-value' : ''}`}>
                          <FiHash className="input-icon" />
                          <input
                            type="text"
                            id="studentId"
                            value={studentId}
                            onChange={(e) => {
                              if (error) setError(null);
                              setStudentId(e.target.value.toUpperCase());
                            }}
                            placeholder="Enter your Student ID (e.g., 2A4571)"
                            required
                            autoComplete="username"
                          />
                        </div>
                      </div>

                      <div className="form-group">
                        <label htmlFor="studentEmail">Email Address</label>
                        <div className={`input-wrapper ${studentEmail ? 'has-value' : ''}`}>
                          <FiMail className="input-icon" />
                          <input
                            type="email"
                            id="studentEmail"
                            value={studentEmail}
                            onChange={handleInputChange(setStudentEmail)}
                            placeholder="Enter your email address"
                            required
                            autoComplete="email"
                          />
                        </div>
                      </div>

                      <p className="verification-hint">
                        A verification code will be sent to your email address.
                      </p>
                    </>
                  )}

                  <button 
                    type="submit" 
                    className="login-button"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <div className="spinner-small"></div>
                        <span>{loginType === 'student' ? 'Sending Code...' : 'Signing in...'}</span>
                      </>
                    ) : (
                      loginType === 'student' ? 'Send Verification Code' : 'Sign In'
                    )}
                  </button>
                </form>

                <div className="login-footer">
                  <p className="help-text">
                    {loginType === 'faculty' 
                      ? 'Access restricted to CCS faculty members only.'
                      : 'Students can only access their own information.'}
                  </p>
                  <p className="contact-admin">
                    Having trouble? Contact the system administrator.
                  </p>
                </div>
              </>
            ) : (
              <>
                {/* Verification Step */}
                <div className="login-header">
                  <button 
                    type="button" 
                    className="back-button"
                    onClick={handleBackToLogin}
                  >
                    <FiArrowLeft /> Back
                  </button>
                  <h2>Enter Verification Code</h2>
                  <p>We've sent a 6-digit code to <strong>{verificationData?.email}</strong></p>
                </div>

                {error && (
                  <div className="error-alert">
                    <FiAlertCircle />
                    <span>{error}</span>
                    <button 
                      type="button" 
                      className="error-close"
                      onClick={() => setError(null)}
                      title="Dismiss"
                    >
                      ×
                    </button>
                  </div>
                )}

                {successMessage && (
                  <div className="success-alert">
                    <FiCheck />
                    <span>{successMessage}</span>
                  </div>
                )}

                <form onSubmit={handleVerificationSubmit} className="login-form">
                  <div className="verification-code-container">
                    {verificationCode.map((digit, index) => (
                      <input
                        key={index}
                        ref={(el) => (codeInputRefs.current[index] = el)}
                        type="text"
                        inputMode="numeric"
                        maxLength={1}
                        value={digit}
                        onChange={(e) => handleCodeChange(index, e.target.value)}
                        onKeyDown={(e) => handleCodeKeyDown(index, e)}
                        onPaste={index === 0 ? handleCodePaste : undefined}
                        className="verification-code-input"
                        autoFocus={index === 0}
                      />
                    ))}
                  </div>

                  <button 
                    type="submit" 
                    className="login-button"
                    disabled={isLoading || verificationCode.join('').length !== 6}
                  >
                    {isLoading ? (
                      <>
                        <div className="spinner-small"></div>
                        <span>Verifying...</span>
                      </>
                    ) : (
                      'Verify & Sign In'
                    )}
                  </button>
                </form>

                <div className="resend-code-section">
                  <p>Didn't receive the code?</p>
                  <button
                    type="button"
                    className="resend-button"
                    onClick={handleResendCode}
                    disabled={resendCooldown > 0 || isLoading}
                  >
                    {resendCooldown > 0 
                      ? `Resend code in ${resendCooldown}s` 
                      : 'Resend Code'}
                  </button>
                </div>

                <div className="login-footer">
                  <p className="help-text">
                    The code will expire in 5 minutes.
                  </p>
                  <p className="contact-admin">
                    Having trouble? Contact the system administrator.
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
